import CloseIcon from '@mui/icons-material/Close'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { gausiumTaskReportPngFilename, reportOpenErrorMessage } from './gausiumTaskReportUtils'

import reportStyle from 'components/ReportViewActions/ReportViewActions.module.css'

export type GausiumTaskReportViewProps = {
  robotSn: string
  reportId: string
  startAt?: Date | null
  className?: string
  /** When set, errors are shown by the parent (e.g. table header banner). */
  onError?: (message: string) => void
}

const GausiumTaskReportView = ({ robotSn, reportId, startAt, className, onError }: GausiumTaskReportViewProps) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [reportPreview, setReportPreview] = useState<{ uri: string } | null>(null)
  const [localFeedback, setLocalFeedback] = useState<string | null>(null)
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showError = (message: string) => {
    if (onError) {
      onError(message)
      return
    }
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current)
      feedbackTimerRef.current = null
    }
    setLocalFeedback(message)
    feedbackTimerRef.current = setTimeout(() => {
      setLocalFeedback(null)
      feedbackTimerRef.current = null
    }, 10000)
  }

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current)
    }
  }, [])

  const openReport = async () => {
    const sn = robotSn.trim()
    const id = reportId.trim()
    if (!sn || !id) return
    setLocalFeedback(null)
    setLoading(true)
    try {
      const { uri } = await MainApi.fetchGausiumTaskReportPngUri({ robot_sn: sn, report_id: id })
      setReportPreview({ uri })
    } catch (err) {
      showError(reportOpenErrorMessage(err, t))
    } finally {
      setLoading(false)
    }
  }

  const saveReport = async () => {
    const sn = robotSn.trim()
    const id = reportId.trim()
    if (!sn || !id) return
    setLocalFeedback(null)
    setSaving(true)
    try {
      const blob = await MainApi.downloadGausiumTaskReportPngBlob({ robot_sn: sn, report_id: id })
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = gausiumTaskReportPngFilename(startAt)
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)
    } catch (err) {
      showError(reportOpenErrorMessage(err, t))
    } finally {
      setSaving(false)
    }
  }

  const busy = loading || saving

  return (
    <>
      {!onError && localFeedback ? (
        <div className={reportStyle.feedback} role="alert" style={{ marginTop: 8 }}>
          <span>{localFeedback}</span>
          <button
            type="button"
            className={reportStyle.feedbackDismiss}
            onClick={() => {
              if (feedbackTimerRef.current) {
                clearTimeout(feedbackTimerRef.current)
                feedbackTimerRef.current = null
              }
              setLocalFeedback(null)
            }}
            aria-label={t('common.hide', 'Hide')}
          >
            ×
          </button>
        </div>
      ) : null}
      <div
        className={clsx(reportStyle.toolbar, className)}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        role="presentation"
      >
        <IconButton
          size="small"
          className={reportStyle.iconBtn}
          disabled={busy}
          aria-label={
            saving ? t('robot.history_report_saving', 'Saving…') : t('robot.history_report_save', 'Save report')
          }
          aria-busy={saving}
          title={saving ? t('robot.history_report_saving', 'Saving…') : t('robot.history_report_save', 'Save report')}
          onClick={e => {
            e.stopPropagation()
            void saveReport()
          }}
        >
          {saving ? <CircularProgress size={18} thickness={4} aria-hidden /> : <SaveOutlinedIcon fontSize="small" />}
        </IconButton>
        <IconButton
          size="small"
          className={reportStyle.iconBtn}
          disabled={busy}
          aria-label={
            loading
              ? t('robot.history_report_loading', 'Loading report…')
              : t('robot.history_report_view', 'View report')
          }
          aria-busy={loading}
          title={
            loading
              ? t('robot.history_report_loading', 'Loading report…')
              : t('robot.history_report_view', 'View report')
          }
          onClick={e => {
            e.stopPropagation()
            void openReport()
          }}
        >
          {loading ? (
            <CircularProgress size={18} thickness={4} aria-hidden />
          ) : (
            <VisibilityOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </div>
      <Dialog
        open={reportPreview != null}
        onClose={() => setReportPreview(null)}
        maxWidth={false}
        fullWidth={false}
        scroll="paper"
        aria-labelledby="task-report-dialog-title"
        PaperProps={{ className: reportStyle.dialogPaper }}
      >
        <DialogTitle id="task-report-dialog-title" className={reportStyle.dialogTitle}>
          <span>{t('robot.history_menu_report', 'Report')}</span>
          <IconButton
            size="small"
            aria-label={t('robot.history_report_close', 'Close')}
            onClick={() => setReportPreview(null)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={reportStyle.dialogBody}>
          {reportPreview ? (
            <img
              src={reportPreview.uri}
              alt=""
              className={reportStyle.previewImg}
              onError={() => {
                setReportPreview(null)
                showError(t('robot.history_report_image_error', 'Could not display the report image.'))
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default GausiumTaskReportView
